import { useEffect, useRef, useState } from "react";
import styles from "./index.module.scss";

interface Option {
    label: string;
    value: string;
}

interface MultiSelectProps {
    label: string;
    options: Option[];
    value: string[];
    name: string;
    onChange: (name: string, value: string[]) => void;
    placeholder?: string;
    minHeight?: number;
}

const MultiSelect = ({ 
    label, 
    options, 
    value = [], 
    name, 
    onChange, 
    placeholder = "Select options...",
    minHeight = 40 
}: MultiSelectProps) => {
    const [selectWidth, setSelectWidth] = useState(0);
    const [showOptions, setShowOptions] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const selectRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleAddOption = (selectedValue: string) => {
        if (!value.includes(selectedValue)) {
            const newValue = [...value, selectedValue];
            onChange(name, newValue);
        }
        setSearchTerm("");
        setShowOptions(false);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleRemoveOption = (optionValue: string) => {
        const newValue = value.filter(v => v !== optionValue);
        onChange(name, newValue);
    };

    const handleOutsideClick = (e: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
            setShowOptions(false);
        }
    };

    const updateSelectWidth = () => {
        if (selectRef?.current) {
            setSelectWidth(selectRef.current.offsetWidth);
        }
    };

    const filteredOptions = (options || []).filter(option => {
        if (!option || !option.label || !option.value) return false;
        if (value && value.includes(option.value)) return false;
        if (!searchTerm) return true;
        return option.label.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const toggleOptions = () => {
        setShowOptions(!showOptions);
    }

    const handleInputClick = () => {
        setShowOptions(true);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && filteredOptions.length > 0) {
            e.preventDefault();
            handleAddOption(filteredOptions[0].value);
        } else if (e.key === 'Backspace' && searchTerm === '' && value.length > 0) {
            e.preventDefault();
            handleRemoveOption(value[value.length - 1]);
        }
    };

    useEffect(() => {
        updateSelectWidth();
    }, []);

    useEffect(() => {
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    return (
        <div className={styles.multiSelectContainer} style={{ maxWidth: selectWidth ? `${selectWidth}px` : "" }}>
            <div className={styles.label}>{label}</div>
            <div
                className={`${styles.customMultiSelect} ${showOptions ? styles.open : ''}`}
                ref={selectRef}
                style={{ minHeight: `${minHeight}px` }}
                onClick={toggleOptions}
            >
                <div className={styles.selectedItems}>
                    {value && value.map((optionValue) => {
                        const option = options.find(opt => opt.value === optionValue);
                        return (
                            <div key={optionValue} className={styles.selectedChip}>
                                <span className={styles.chipText}>{option?.label || optionValue}</span>
                                <button
                                    type="button"
                                    className={styles.removeButton}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveOption(optionValue);
                                    }}
                                    aria-label={`Remove ${option?.label || optionValue}`}
                                >
                                    ×
                                </button>
                            </div>
                        );
                    })}
                    <input
                        ref={inputRef}
                        type="text"
                        className={styles.searchInput}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleInputClick();
                        }}
                        onKeyDown={handleInputKeyDown}
                        placeholder={value.length === 0 ? placeholder : ""}
                    />
                </div>
                <div className={`${styles.dropdownIcon} ${showOptions ? styles.open : ''}`}></div>
                
                {showOptions && (
                    <div className={styles.optionsList}>
                        {filteredOptions && filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    className={styles.option}
                                    onClick={() => handleAddOption(option.value)}
                                >
                                    {option.label || 'Unnamed Option'}
                                </div>
                            ))
                        ) : (
                            <div className={styles.noOptions}>
                                {searchTerm ? "No matching options" : "No options available"}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MultiSelect;